import { useState, useEffect } from 'react'
import { useUIStore } from '@/stores/ui.store'
import { useParceiros, useCreateParceiro, useUpdateParceiro, useDeleteParceiro, useRegenerateApiKey } from '@/hooks/useParceiros'
import {
    Plus, Edit, Trash2, Search, Loader2, Handshake, Key, Copy, Check, RefreshCw, Eye, EyeOff, X, Wallet, Plug
} from 'lucide-react'
import type { Parceiro } from '@/types/database'

export function ParceirosPage() {
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    const [search, setSearch] = useState('')
    const [showForm, setShowForm] = useState(false)
    const [editing, setEditing] = useState<Parceiro | null>(null)
    const [showApiKey, setShowApiKey] = useState<string | null>(null)
    const [copiedId, setCopiedId] = useState<string | null>(null)

    useEffect(() => { setPageTitle('Parceiros') }, [setPageTitle])

    const { data: result, isLoading } = useParceiros({ search })
    const createMut = useCreateParceiro()
    const updateMut = useUpdateParceiro()
    const deleteMut = useDeleteParceiro()
    const regenMut = useRegenerateApiKey()

    const parceiros = result?.data || []

    const handleDelete = async (id: string) => {
        if (confirm('Tem certeza que deseja desativar este parceiro?')) {
            await deleteMut.mutateAsync(id)
        }
    }

    const handleRegenKey = async (id: string) => {
        if (confirm('Tem certeza? A chave atual será invalidada imediatamente.')) {
            const newKey = await regenMut.mutateAsync(id)
            setShowApiKey(id)
            // Copy the new key
            navigator.clipboard.writeText(newKey)
            setCopiedId(id)
            setTimeout(() => setCopiedId(null), 3000)
        }
    }

    const copyToClipboard = (text: string, id: string) => {
        navigator.clipboard.writeText(text)
        setCopiedId(id)
        setTimeout(() => setCopiedId(null), 2000)
    }

    const handleSave = async (input: Partial<Parceiro>) => {
        if (editing) {
            await updateMut.mutateAsync({ ...input, id: editing.id })
        } else {
            await createMut.mutateAsync(input)
        }
        setShowForm(false)
        setEditing(null)
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-surface-900">Parceiros</h2>
                    <p className="text-sm text-surface-700/60 mt-0.5">Parceiros para split de pagamento e integrações via API</p>
                </div>
                <button onClick={() => { setEditing(null); setShowForm(true) }} className="btn btn-primary btn-md gap-2">
                    <Plus size={18} /> Novo Parceiro
                </button>
            </div>

            {/* Asaas Webhook Alert */}
            <div className="glass-card p-4 bg-primary-50 border-primary-100 flex items-center justify-between group">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-xl text-primary-600 shadow-sm">
                        <Plug size={20} />
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-primary-900">Configuração de Webhook no Asaas</p>
                        <p className="text-xs text-primary-700/70">URL necessária para receber confirmações de pagamento: 
                            <code className="ml-2 font-mono font-bold bg-white/50 px-1 rounded transition-colors group-hover:bg-white text-primary-800">
                                https://xqlsvhezsjzrfuucpmou.supabase.co/functions/v1/asaas-webhook
                            </code>
                        </p>
                    </div>
                </div>
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText('https://xqlsvhezsjzrfuucpmou.supabase.co/functions/v1/asaas-webhook')
                    setPageTitle('Copiado!')
                    setTimeout(() => setPageTitle('Parceiros'), 2000)
                  }}
                  className="btn btn-sm btn-white gap-2 shadow-sm"
                >
                    <Copy size={14} /> Copiar URL
                </button>
            </div>

            {/* Content */}
            <div className="glass-card">
                {/* Toolbar */}
                <div className="p-4 border-b border-surface-100 flex gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-700/40" />
                        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar por nome, CNPJ ou wallet..."
                            className="input pl-10" />
                    </div>
                </div>

                {/* List */}
                {isLoading ? (
                    <div className="flex justify-center p-12">
                        <Loader2 size={24} className="animate-spin text-primary-600" />
                    </div>
                ) : parceiros.length === 0 ? (
                    <div className="text-center py-16 text-surface-700/60">
                        <Handshake size={40} className="mx-auto mb-3 opacity-20" />
                        <p>Nenhum parceiro encontrado</p>
                        <p className="text-xs mt-1">Cadastre parceiros para configurar split de pagamentos</p>
                    </div>
                ) : (
                    <div className="divide-y divide-surface-100">
                        {parceiros.map(item => (
                            <div key={item.id} className="p-4 hover:bg-surface-50 transition-colors">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-start gap-4">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0
                                            ${item.ativo ? 'bg-emerald-100 text-emerald-600' : 'bg-surface-100 text-surface-400'}`}>
                                            <Handshake size={20} />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-semibold text-surface-900">{item.nome}</h3>
                                                <span className={`badge ${item.ativo ? 'badge-success' : 'badge-danger'}`}>
                                                    {item.ativo ? 'Ativo' : 'Inativo'}
                                                </span>
                                            </div>
                                            {item.cnpj && (
                                                <p className="text-xs text-surface-700/60 mt-0.5">CNPJ: {item.cnpj}</p>
                                            )}
                                            <div className="flex items-center gap-4 mt-1.5">
                                                <span className="flex items-center gap-1 text-xs text-surface-700/60">
                                                    <Wallet size={12} />
                                                    <span className="font-mono">{item.wallet_id}</span>
                                                </span>
                                                <div className="flex items-center gap-1">
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider
                                                        ${item.asaas_empresa === 'ACSM' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                                                        {item.asaas_empresa}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {/* API Key display */}
                                        <div className="flex items-center gap-1.5 mr-2">
                                            <Key size={14} className="text-surface-400" />
                                            <code className="text-xs font-mono text-surface-500 max-w-[140px] truncate">
                                                {showApiKey === item.id ? item.api_key : '••••••••••••••••'}
                                            </code>
                                            <button
                                                onClick={() => setShowApiKey(showApiKey === item.id ? null : item.id)}
                                                className="p-1 rounded hover:bg-surface-100 text-surface-400 hover:text-surface-600"
                                                title={showApiKey === item.id ? 'Ocultar' : 'Mostrar'}
                                            >
                                                {showApiKey === item.id ? <EyeOff size={13} /> : <Eye size={13} />}
                                            </button>
                                            <button
                                                onClick={() => copyToClipboard(item.api_key, `key-${item.id}`)}
                                                className="p-1 rounded hover:bg-surface-100 text-surface-400 hover:text-primary-600"
                                                title="Copiar API Key"
                                            >
                                                {copiedId === `key-${item.id}` ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
                                            </button>
                                            <button
                                                onClick={() => handleRegenKey(item.id)}
                                                disabled={regenMut.isPending}
                                                className="p-1 rounded hover:bg-amber-50 text-surface-400 hover:text-amber-600"
                                                title="Regenerar API Key"
                                            >
                                                <RefreshCw size={13} className={regenMut.isPending ? 'animate-spin' : ''} />
                                            </button>
                                        </div>

                                        <div className="h-4 w-px bg-surface-200 mx-1" />
                                        <button onClick={() => { setEditing(item); setShowForm(true) }}
                                            className="p-1.5 rounded-lg hover:bg-surface-100 text-surface-700/60 hover:text-primary-600">
                                            <Edit size={16} />
                                        </button>
                                        <button onClick={() => handleDelete(item.id)}
                                            className="p-1.5 rounded-lg hover:bg-danger-50 text-surface-700/60 hover:text-danger-600">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Form Modal */}
            {showForm && (
                <ParceiroFormModal
                    parceiro={editing}
                    onSave={handleSave}
                    onClose={() => { setShowForm(false); setEditing(null) }}
                    isPending={createMut.isPending || updateMut.isPending}
                />
            )}
        </div>
    )
}

/* ─── Form Modal ─── */
function ParceiroFormModal({ parceiro, onSave, onClose, isPending }: {
    parceiro: Parceiro | null
    onSave: (input: Partial<Parceiro>) => Promise<void>
    onClose: () => void
    isPending: boolean
}) {
    const [nome, setNome] = useState(parceiro?.nome || '')
    const [cnpj, setCnpj] = useState(parceiro?.cnpj || '')
    const [email, setEmail] = useState(parceiro?.email || '')
    const [telefone, setTelefone] = useState(parceiro?.telefone || '')
    const [walletId, setWalletId] = useState(parceiro?.wallet_id || '')
    const [asaasEmpresa, setAsaasEmpresa] = useState<'ACSM' | 'MULTIVEICULAR'>(parceiro?.asaas_empresa || 'ACSM')
    const [webhookUrl, setWebhookUrl] = useState(parceiro?.webhook_url || '')

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        await onSave({
            nome,
            cnpj: cnpj.trim() || null,
            email: email.trim() || null,
            telefone: telefone.trim() || null,
            wallet_id: walletId.trim(),
            asaas_empresa: asaasEmpresa,
            webhook_url: webhookUrl.trim() || null,
        })
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto rounded-3xl shadow-elevated">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold">{parceiro ? 'Editar Parceiro' : 'Novo Parceiro'}</h3>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100"><X size={18} /></button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-surface-700 mb-1.5">Nome *</label>
                        <input type="text" value={nome} onChange={(e) => setNome(e.target.value)}
                            required placeholder="Nome do parceiro" className="input" />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-surface-700 mb-1.5">CNPJ</label>
                        <input type="text" value={cnpj} onChange={(e) => setCnpj(e.target.value)}
                            placeholder="00.000.000/0000-00" className="input" />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-surface-700 mb-1.5">Email</label>
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                placeholder="parceiro@email.com" className="input" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-surface-700 mb-1.5">Telefone</label>
                            <input type="text" value={telefone} onChange={(e) => setTelefone(e.target.value)}
                                placeholder="(00) 00000-0000" className="input" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 items-end">
                        <div className="space-y-1.5 p-4 rounded-xl bg-amber-50/50 border border-amber-100">
                            <label className="flex items-center gap-2 text-sm font-medium text-amber-800">
                                <Wallet size={16} />
                                Wallet ID do Asaas *
                            </label>
                            <input type="text" value={walletId} onChange={(e) => setWalletId(e.target.value)}
                                required placeholder="Ex: 5a49b346-cd3c-..." className="input font-mono text-sm" />
                        </div>
                        <div className="space-y-1.5">
                            <label className="block text-sm font-medium text-surface-700">Empresa Asaas *</label>
                            <select value={asaasEmpresa} onChange={(e) => setAsaasEmpresa(e.target.value as any)}
                                className="input">
                                <option value="ACSM">ACSM</option>
                                <option value="MULTIVEICULAR">Multiveicular</option>
                            </select>
                        </div>
                    </div>
                    <p className="text-[11px] text-amber-600/70 -mt-2 px-4">
                        ID da carteira e conta destino para o split de pagamentos.
                    </p>

                    <div className="space-y-1.5 pt-2">
                        <label className="flex items-center gap-2 text-sm font-medium text-surface-700">
                            <Plug size={16} className="text-success-600" />
                            URL de Webhook do Parceiro (Opcional)
                        </label>
                        <input type="url" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)}
                            placeholder="https://seu-sistema.com/webhook" className="input" />
                        <p className="text-[10px] text-surface-500 px-1">
                            Disparado quando um contrato for <strong>ativado</strong> pelo pagamento da adesão.
                        </p>
                    </div>

                    {!parceiro && (
                        <div className="flex items-start gap-2 p-3 rounded-xl bg-blue-50 border border-blue-100">
                            <Key size={16} className="text-blue-500 mt-0.5 shrink-0" />
                            <p className="text-xs text-blue-700">
                                Uma <strong>API Key</strong> será gerada automaticamente ao criar o parceiro.
                                Ela será usada para autenticar chamadas da API de contratação.
                            </p>
                        </div>
                    )}

                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose}
                            className="btn flex-1 border border-surface-200 hover:bg-surface-50">Cancelar</button>
                        <button type="submit" disabled={isPending}
                            className="btn btn-primary flex-1 gap-2">
                            {isPending ? <Loader2 size={16} className="animate-spin" /> : parceiro ? 'Salvar' : 'Criar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}
